import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useFormState } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { InternalIdInput, JsonEditor } from '@library'
import { MobileConfiguratorLayout } from '@mobileComponents/MobileConfiguratorLayout'
import { SaveDraftDialog } from '@mobileComponents/SaveDraftDialog'
import { DEFAULT_MOBILE_SCREEN, SCREEN_TYPE_OPTIONS } from '@mobileConstants'
import { EventFlowsEditor } from '@mobileComponents/EventFlow'
import { extractServerErrorMessage, validateMobileScreen } from '@mobileHelpers'
import { useDraft, useMobileForms, useMobileListItems, useMobileMaps, useMobileScreens, useMobileSearchAssistants } from '@mobileHooks'
import {
  MOBILE_EVENT_CODE,
  MobileMapPointGroup,
  MobileMapRouteTarget,
  MobileScreen,
} from '@mobileTypes'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { ColorPickerField } from '@components/ColorPicker'
import { Add as AddIcon } from '@mui/icons-material'
import { DataGrid, GridColDef } from '@microservices/wiskey-react-table-component'
import { VariableCreateDialog } from '@mobileComponents/Variables'
import { generateDataGridActionsColumn } from '@helpers'

import { ROUTES } from '@constants'
import { Link } from 'react-router-dom'

export const MobileScreenCreateOrEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = !!id

  const { createScreen, updateScreen, getScreenById } = useMobileScreens()
  const { saveDraft, clearDraft, draft, hasDraft } = useDraft<MobileScreen>(`mobile-screen-draft-${id ?? 'new'}`)
  const shouldAutoRestore = useRef((location.state as any)?.restoreDraft === true)
  const [jsonValue, setJsonValue] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)

  const { control, handleSubmit, reset, watch, setValue } = useForm<MobileScreen>({
    defaultValues: isEdit ? getScreenById(id) || DEFAULT_MOBILE_SCREEN : DEFAULT_MOBILE_SCREEN,
  })

  const { isDirty } = useFormState({ control })

  const formData = watch()
  const screenType = watch('type')
  const backgroundType = watch('backgroundType')

  const { listItems } = useMobileListItems()
  const { forms } = useMobileForms()
  const { searchAssistants } = useMobileSearchAssistants()
  const { maps: mobileMaps } = useMobileMaps()

  useEffect(() => {
    if (!isEdit && shouldAutoRestore.current && draft) {
      Object.keys(draft.data).forEach(key => {
        setValue(key as any, (draft.data as any)[key], { shouldDirty: true, shouldValidate: false })
      })
      setJsonValue(JSON.stringify(draft.data, null, 2))
      shouldAutoRestore.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEdit && id) {
      const screen = getScreenById(id)
      if (screen) {
        if (shouldAutoRestore.current && draft) {
          Object.keys(draft.data).forEach(key => {
            setValue(key as any, (draft.data as any)[key], { shouldDirty: true, shouldValidate: false })
          })
          setJsonValue(JSON.stringify(draft.data, null, 2))
          shouldAutoRestore.current = false
        } else {
          reset(screen)
          setJsonValue(JSON.stringify(screen, null, 2))
        }
      }
    }
  }, [id, isEdit, getScreenById, reset])

  useEffect(() => {
    if (screenType !== 'SPLIT') return
    if (formData.backgroundType === undefined || formData.backgroundType === '') {
      setValue('backgroundType', 'LIST')
    }
    if (!Array.isArray(formData.snapPoints) || formData.snapPoints.length === 0) {
      setValue('snapPoints', ['25%', '50%', '90%'])
    }
    if (formData.initialSnapIndex === undefined || formData.initialSnapIndex === null) {
      setValue('initialSnapIndex', 1)
    }
  }, [screenType])

  const [errors, setErrors] = useState<string[]>([])

  const splitScreenVariables = useMemo(() => {
    if (screenType !== 'SPLIT') return { backgroundVariables: undefined, sheetFormVariables: undefined }
    const bgType = formData.backgroundType || 'LIST'
    let backgroundVars: Array<{ id?: string; internalId: string }> | undefined
    if (bgType === 'LIST' && formData.backgroundListItemConfigId) {
      const listItem = listItems.find(li => li.internalId === formData.backgroundListItemConfigId)
      backgroundVars = listItem?.variables?.map(v => ({ id: v.id ?? v.internalId, internalId: v.internalId }))
    } else if (bgType === 'FORM' && formData.backgroundFormConfigId) {
      const bgForm = forms.find(f => f.id === formData.backgroundFormConfigId)
      backgroundVars = bgForm?.variables?.map(v => ({ id: v.id ?? v.internalId, internalId: v.internalId }))
    }
    let sheetVars: Array<{ id?: string; internalId: string }> | undefined
    if (formData.sheetFormConfigId) {
      const sheetForm = forms.find(f => f.id === formData.sheetFormConfigId)
      sheetVars = sheetForm?.variables?.map(v => ({ id: v.id ?? v.internalId, internalId: v.internalId }))
    }
    return { backgroundVariables: backgroundVars, sheetFormVariables: sheetVars }
  }, [screenType, formData.backgroundType, formData.backgroundListItemConfigId, formData.backgroundFormConfigId, formData.sheetFormConfigId, listItems, forms])

  const onSubmit = async (data: MobileScreen) => {
    const validationErrors = validateMobileScreen(data)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)

      return
    }

    setErrors([])

    try {
      if (isEdit && id) {
        const updatedScreen = await updateScreen(id, data)
        reset(updatedScreen)
        setJsonValue(JSON.stringify(updatedScreen, null, 2))
        clearDraft()
      } else {
        const newScreen = await createScreen(data)
        clearDraft()
        navigate(`${ROUTES.CONFIG_MOBILE_SCREENS_EDIT}/${newScreen.id}`, { replace: true })
      }
    } catch (error: unknown) {
      setSaveError(extractServerErrorMessage(error))
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.CONFIG_MOBILE_SCREENS)
  }

  const handleReset = () => {
    if (isEdit && id) {
      const screen = getScreenById(id)
      if (screen) {
        reset(screen)
        setJsonValue(JSON.stringify(screen, null, 2))
        setErrors([])
      }
    } else {
      reset(DEFAULT_MOBILE_SCREEN)
      setJsonValue(JSON.stringify(DEFAULT_MOBILE_SCREEN, null, 2))
      setErrors([])
    }
  }

  const handleJsonChange = (newValue: string) => {
    try {
      const parsed = JSON.parse(newValue)
      // Use setValue for each field to mark form as dirty
      Object.keys(parsed).forEach(key => {
        setValue(key as any, parsed[key], { shouldDirty: true, shouldValidate: false })
      })
      setJsonValue(newValue)
    } catch (error) {
      // Invalid JSON, just update the state
      setJsonValue(newValue)
    }
  }

  return (
    <>
      <SaveDraftDialog
        open={!!saveError}
        errorMessage={saveError ?? ''}
        onSaveDraft={() => saveDraft(watch())}
        onClose={() => setSaveError(null)}
      />
      <MobileConfiguratorLayout
        title={isEdit ? 'Edit Mobile Screen' : 'Create Mobile Screen'}
        isEdit={isEdit}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        onReset={handleReset}
        isDirty={isDirty}
        errors={errors}
        draftProps={{
          hasDraft,
          draftSavedAt: draft?.savedAt,
          onRestoreDraft: () => {
            if (draft) {
              reset(draft.data)
              setJsonValue(JSON.stringify(draft.data, null, 2))
            }
          },
          onDiscardDraft: clearDraft,
          onSaveDraft: () => saveDraft(watch()),
        }}
      >
      <Box sx={{ py: 3 }}>
        <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Controller
            name='internalId'
            control={control}
            rules={{ required: 'Internal ID is required' }}
            render={({ field, fieldState: { error } }) => (
              <InternalIdInput
                value={field.value ?? ''}
                onChange={field.onChange}
                fullWidth
                label='Internal ID'
                error={!!error}
                helperText={error?.message}
                required
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name='name'
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label='Screen Name'
                error={!!error}
                helperText={error?.message || 'e.g., WorkPackageScreen'}
                required
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name='title'
            control={control}
            rules={{ required: 'Title is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label='Screen Title'
                error={!!error}
                helperText={error?.message}
                required
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name='backgroundColor'
            control={control}
            render={({ field }) => (
              <ColorPickerField
                label='Background Color'
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name='type'
            control={control}
            rules={{ required: 'Type is required' }}
            render={({ field }) => (
              <FormControl fullWidth required>
                <InputLabel>Screen Type</InputLabel>
                <Select {...field} label='Screen Type'>
                  {SCREEN_TYPE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        {screenType === 'LIST' && (
          <Grid item xs={12} md={6}>
            <Controller
              name='listItemConfigId'
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>List Item Config</InputLabel>
                  <Select {...field} label='List Item Config'>
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {listItems.map(item => (
                      <MenuItem key={item.internalId} value={item.internalId}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        )}

        {screenType === 'FORM' && (
          <Grid item xs={12} md={6}>
            <Controller
              name='formConfigId'
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Form Config</InputLabel>
                  <Select {...field} label='Form Config'>
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {forms.map(form => (
                      <MenuItem key={form.id} value={form.id}>
                        {form.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        )}

        {screenType === 'LIST' && (
          <Grid item xs={12} md={6}>
            <Controller
              name='searchAssistantId'
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Search Assistant</InputLabel>
                  <Select {...field} label='Search Assistant'>
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {searchAssistants.map(sa => (
                      <MenuItem key={sa.id} value={sa.id}>
                        {sa.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        )}

        {screenType === 'MAP' && (
          <Grid item xs={12} md={6}>
            <Controller
              name='mapConfigId'
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Map config</InputLabel>
                  <Select {...field} label='Map config' value={field.value ?? ''}>
                    <MenuItem value=''>
                      <em>None (configure inline)</em>
                    </MenuItem>
                    {mobileMaps.map(map => (
                      <MenuItem key={map.id} value={map.id}>
                        {map.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        )}

        {screenType === 'SPLIT' && (
          <>
            <Grid item xs={12} md={6}>
              <Controller
                name='backgroundType'
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Background type</InputLabel>
                    <Select {...field} label='Background type'>
                      <MenuItem value='LIST'>List</MenuItem>
                      <MenuItem value='FORM'>Form</MenuItem>
                      <MenuItem value='MAP'>Map</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            {backgroundType === 'LIST' && (
              <Grid item xs={12} md={6}>
                <Controller
                  name='backgroundListItemConfigId'
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Background list config</InputLabel>
                      <Select {...field} label='Background list config'>
                        <MenuItem value=''>
                          <em>None</em>
                        </MenuItem>
                        {listItems.map(item => (
                          <MenuItem key={item.internalId} value={item.internalId}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}
            {backgroundType === 'FORM' && (
              <Grid item xs={12} md={6}>
                <Controller
                  name='backgroundFormConfigId'
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Background form config</InputLabel>
                      <Select {...field} label='Background form config'>
                        <MenuItem value=''>
                          <em>None</em>
                        </MenuItem>
                        {forms.map(form => (
                          <MenuItem key={form.id} value={form.id}>
                            {form.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}
            {backgroundType === 'MAP' && (
              <Grid item xs={12} md={6}>
                <Controller
                  name='backgroundMapConfigId'
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Background map config</InputLabel>
                      <Select {...field} label='Background map config' value={field.value ?? ''}>
                        <MenuItem value=''>
                          <em>None (configure inline)</em>
                        </MenuItem>
                        {mobileMaps.map(map => (
                          <MenuItem key={map.id} value={map.id}>
                            {map.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Controller
                name='sheetFormConfigId'
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required>
                    <InputLabel>Sheet form config</InputLabel>
                    <Select {...field} label='Sheet form config'>
                      <MenuItem value=''>
                        <em>None</em>
                      </MenuItem>
                      {forms.map(form => (
                        <MenuItem key={form.id} value={form.id}>
                          {form.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name='snapPoints'
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label='Snap points'
                    placeholder='25%, 50%, 90%'
                    value={Array.isArray(field.value) ? field.value.join(', ') : ''}
                    onChange={e => {
                      const raw = e.target.value
                      const arr = raw
                        ? raw.split(',').map(s => s.trim()).filter(Boolean)
                        : ['25%', '50%', '90%']
                      field.onChange(arr)
                    }}
                    helperText='Comma-separated, e.g. 25%, 50%, 90%'
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name='initialSnapIndex'
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    type='number'
                    label='Initial snap index'
                    inputProps={{ min: 0 }}
                    value={field.value ?? 1}
                    onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}
                    helperText='0-based index of initial snap point'
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Controller
                  name='eventFlows'
                  control={control}
                  render={({ field }) => (
                    <EventFlowsEditor
                      eventFlows={field.value ?? []}
                      onEventFlowsChange={field.onChange}
                      allowedEventCodes={[
                        MOBILE_EVENT_CODE.ON_SHEET_OPENED,
                        MOBILE_EVENT_CODE.ON_SHEET_CLOSED,
                        MOBILE_EVENT_CODE.ON_VARIABLE_CHANGE,
                        MOBILE_EVENT_CODE.ON_INTERVAL,
                        MOBILE_EVENT_CODE.ON_INIT,
                        MOBILE_EVENT_CODE.ON_UNMOUNT,
                      ]}
                      showAddListenerButton={true}
                      backgroundVariables={splitScreenVariables.backgroundVariables}
                      sheetFormVariables={splitScreenVariables.sheetFormVariables}
                    />
                  )}
                />
              </Paper>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <JsonEditor
              value={jsonValue}
              onChange={handleJsonChange}
              label='Screen Configuration (JSON)'
              minHeight={300}
            />
          </Paper>
        </Grid>
      </Grid>
      </Box>
    </MobileConfiguratorLayout>
    </>
  )
}
